import { useEffect, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import "./unitlisting.scss";
import threedotvertical from "../../../assets/images/three-dots-vertical.svg";
import editicon from "../../../assets/images/edit-icon.svg";
import viewicon from "../../../assets/images/view-icon.svg";
import deactivateicon from "../../../assets/images/deactivate-icon.svg";
import deleteicon from "../../../assets/images/delete-icon.svg";
import { ReactSVG } from "react-svg";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import useUnits from "../../../Hooks/useUnits";
import { setSelectedUnit } from "../../../redux/units/unitsSlice";
import Loading from "../../../components/LoadingPage/Loading";
import { toggleClass } from "../../../utils/commonUtils";
import { DeleteConfModel } from "../../../components/DeleteConfModel/deleteConfModel";
import CommonHeaderWithImpoExpo from "../../../components/commonHeaderWithImpoExpo/commonHeaderWithImpoExpo";
import moment from "moment";
import { toast } from "react-toastify";
import { useModulePermissions, PermissionIdentifier, PermissionModule } from "../../../utils/permissionUtils";

const UnitListing = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const { getUnitsAPI, getUnitsLoading } = useUnits();
  const { activeUnitAPI, setUnitStatusActiveLoading } = useUnits();
  const { deactiveUnitAPI, setUnitStatusDeActiveLoading } = useUnits();
  const { importUnitAPI, importUnitLoading } = useUnits();
  const { exportUnitAPI, exportUnitLoading } = useUnits();
  const { deleteUnitAPI, deleteUnitLoading } = useUnits();
  const [showModal, setShowModal] = useState(false);
  const [unitsList, setUnitsList] = useState([]);
  const [currentUnit, setCurrentUnit] = useState<any>();
  const [showImportFileModal, setShowImportFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const defaultFilter = { searchText: "", active: true };
  const [searchFieldVal, setSearchFieldVal] = useState(defaultFilter);
  const [importErrorDetails, setImportErrorDetails] = useState<any[]>([]);

  const deleteUnit = async () => {
    await deleteUnitAPI({
      id: currentUnit.id,
      updatedBy: currentUserId ?? "",
    });
    toggleModal();
    getUnits();
  };

  const toggleModal = () => {
    toggleClass();
    setShowModal(!showModal);
  };

  const getUnits = () => {
    getUnitsAPI(searchFieldVal)
      .then((res: any) => setUnitsList(res))
      .catch();
  };

  const editUnit = (Unit: any) => {
    dispatch(setSelectedUnit(Unit));
    navigate("/edit-unit");
  };

  const navigateToView = (Unit: any) => {
    dispatch(setSelectedUnit(Unit));
    navigate("/view-unit");
  };

  const updateStatus = async (Unit: any) => {
    if (Unit.isActive) {
      await deactiveUnitAPI({ id: Unit.id, updatedBy: currentUserId ?? "" });
    } else {
      await activeUnitAPI({ id: Unit.id, updatedBy: currentUserId ?? "" });
    }
    getUnits();
  };

  const handleExportUnitList = async () => {
    const response = await exportUnitAPI();
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${localStorage.getItem("profileClientId")?.toUpperCase() ?? ""}_Unit_List_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
    document.body.appendChild(link);
    link.click();
  };

  const toggleImportModal = () => {
    setSelectedFile(null);
    setImportErrorDetails([]);
    toggleClass();
    setShowImportFileModal(!showImportFileModal);
  };

  const handleFileImport = async (file: any) => {
    const formData = new FormData();
    formData.append("file", file || "");
    const response = await importUnitAPI({ 'file': formData, 'createdBy': currentUserId });
    if (response.error && response.error.data?.status !== 200 && response.error.data?.result?.isSuccess === false) {
      if (response.error.data?.status === 400) {
        toast.error(response.error.data?.result?.errorMessage);
      }
      toast.error("Error!. File imported unsuccessfully");
      const errorData = response.error.data?.result?.data?.filter((data: any) => data.isValid === false);
      setImportErrorDetails(errorData);
    } else {
      toast.success("Success!. File imported successfully");
      toggleImportModal();
      getUnits();
    }
  };

  useEffect(() => {
    getUnits();
  }, [searchFieldVal]);

  const isPermitted = useModulePermissions(PermissionModule.UNIT_MASTER);
  const isViewPermitted = isPermitted(PermissionIdentifier.VIEW_UNIT_MASTER);
  const isCreatePermitted = isPermitted(PermissionIdentifier.CREATE_UNIT_MASTER);
  const isEditPermitted = isPermitted(PermissionIdentifier.EDIT_UNIT_MASTER);
  const isDeletePermitted = isPermitted(PermissionIdentifier.DELETE_UNIT_MASTER);
  const isInactivePermitted = isPermitted(PermissionIdentifier.INACTIVE_UNIT_MASTER);
  const isImportPermitted = isPermitted(PermissionIdentifier.IMPORT_UNIT_MASTER);
  const isExportPermitted = isPermitted(PermissionIdentifier.EXPORT_UNIT_MASTER);
  const isAnyActionPermitted = isViewPermitted || isEditPermitted || isDeletePermitted || isInactivePermitted;

  return (
    <div className="unit-listing-wrapper">
      {getUnitsLoading ||
        setUnitStatusActiveLoading ||
        setUnitStatusDeActiveLoading ||
        exportUnitLoading ||
        importUnitLoading ||
        deleteUnitLoading ? (
        <Loading />
      ) : null}
      <CommonHeaderWithImpoExpo
        title="Unit Master"
        buttonTitle="Add New"
        href="/add-unit"
        disRecords={unitsList.length || 0}
        totalRecords={unitsList.length || 0}
        searchVal={searchFieldVal}
        setSearchVal={setSearchFieldVal}
        defaultFilter={defaultFilter}
        isAddPermitted={isCreatePermitted}
        exportList={handleExportUnitList}
        toggleImportXlsxModal={toggleImportModal}
        isImportPermitted={isImportPermitted}
        isExportPermitted={isExportPermitted}
        showImportFileModal={showImportFileModal}
        importList={handleFileImport}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        importErrorDetails={{ data: importErrorDetails, module: "units" }}
      />
      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th>Unit Id</th>
              <th>Unit Name</th>
              <th>Sort Order</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {unitsList?.map((x: any) => (
              <tr
                key={x.id}
                onDoubleClick={() => {
                  navigateToView(x);
                }}
              >
                <td>{x.unitCode}</td>
                <td title={x.unitName}>{x.unitName}</td>
                <td>{x.sortOrder}</td>
                <td>
                  <span
                    className={`status-circle ${x.isActive ? "active" : "inactive"}`}
                  >
                    {x.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <Dropdown>
                    {isAnyActionPermitted && (
                      <Dropdown.Toggle variant="link" id="dropdown-basic">
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={threedotvertical}
                        />
                      </Dropdown.Toggle>
                    )}
                    <Dropdown.Menu>
                      {isViewPermitted && (
                        <Dropdown.Item onClick={() => navigateToView(x)}>
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={viewicon}
                          />
                          View
                        </Dropdown.Item>
                      )}
                      {isInactivePermitted && (
                        <Dropdown.Item
                          onClick={() => {
                            updateStatus(x);
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={deactivateicon}
                          />
                          {x.isActive ? "Deactivate" : "Activate"}
                        </Dropdown.Item>
                      )}
                      {isEditPermitted && (
                        <Dropdown.Item onClick={() => editUnit(x)}>
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={editicon}
                          />
                          Edit
                        </Dropdown.Item>
                      )}
                      {isDeletePermitted && (
                        <Dropdown.Item
                          onClick={() => {
                            setCurrentUnit(x);
                            toggleModal();
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={deleteicon}
                          />
                          Delete
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <DeleteConfModel
        showModal={showModal}
        toggleModal={toggleModal}
        deleteAction={deleteUnit}
        recordName={currentUnit?.unitName || ""}
        modelName={`unit`}
        isDeletePermitted={isDeletePermitted}
      />
    </div>
  );
};

export default UnitListing;
